import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, FileText, Search, Printer, Download, Clock, History,
  ChevronRight, ChevronDown, Loader2, X, Sparkles,
} from "lucide-react";
import "./AdminUAT.css";

// Icon per category id — falls back to FileText.
const CATEGORY_ICON = {
  uat_docs: BookOpen,
  release_notes: Sparkles,
  signoff: FileText,
  prod_readiness: FileText,
  uat_reports: FileText,
  previous_releases: History,
  other: FileText,
};

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function relativeDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminUAT() {
  const [index, setIndex] = useState(null);
  const [loadingIndex, setLoadingIndex] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const contentRef = useRef(null);

  // ---- Load index -----------------------------------------------------
  const loadIndex = useCallback(async () => {
    setLoadingIndex(true);
    try {
      const { data } = await api.get("/admin/release-docs/index");
      setIndex(data);
      // Auto-select current release note if nothing selected yet.
      if (!selectedSlug && data.current_release) {
        setSelectedSlug(data.current_release.slug);
      } else if (!selectedSlug && data.categories?.[0]?.docs?.[0]) {
        setSelectedSlug(data.categories[0].docs[0].slug);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load release docs index"));
    } finally {
      setLoadingIndex(false);
    }
  }, [selectedSlug]);

  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  // ---- Load a specific doc -------------------------------------------
  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    (async () => {
      setLoadingDoc(true);
      setHistoryOpen(false);
      setHistory(null);
      try {
        const { data } = await api.get("/admin/release-docs/doc", { params: { slug: selectedSlug } });
        if (!cancelled) {
          setDoc(data);
          // Scroll the content pane back to the top on doc change.
          if (contentRef.current) contentRef.current.scrollTop = 0;
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, "Failed to load document"));
      } finally {
        if (!cancelled) setLoadingDoc(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSlug]);

  // ---- Search (debounced) --------------------------------------------
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/release-docs/search", { params: { q } });
        setSearchResults(data);
      } catch (e) {
        toast.error(formatApiError(e, "Search failed"));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  // ---- Actions --------------------------------------------------------
  const handlePrint = () => {
    // Print-friendly view — the CSS `@media print` block hides everything
    // except the rendered content column.
    window.print();
  };

  const handleDownloadRaw = () => {
    if (!selectedSlug) return;
    const token = localStorage.getItem("hw_access_token");
    const url = `${api.defaults.baseURL}/admin/release-docs/raw?slug=${encodeURIComponent(selectedSlug)}`;
    // Use fetch with auth so we don't rely on cookies.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        const filename = selectedSlug.split("/").pop() || "document.md";
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 500);
      })
      .catch(() => toast.error("Download failed"));
  };

  const loadHistory = async () => {
    if (!selectedSlug) return;
    setHistoryOpen(true);
    if (history && history.slug === selectedSlug) return;
    try {
      const { data } = await api.get("/admin/release-docs/history", {
        params: { slug: selectedSlug, limit: 25 },
      });
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load history"));
    }
  };

  const toggleCategory = (id) => {
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  };

  // Categories — merged with the current search result (if any)
  const categoriesForSidebar = useMemo(() => {
    if (searchResults) {
      return [{
        id: "search",
        label: `Search results (${searchResults.count})`,
        docs: searchResults.results,
        __search: true,
      }];
    }
    return index?.categories || [];
  }, [index, searchResults]);

  return (
    <div className="hw-uat" data-testid="admin-uat-root">
      {/* -------------------------- HEADER -------------------------- */}
      <div className="hw-uat__header no-print">
        <div>
          <h1 data-testid="admin-uat-title">UAT & Release Docs</h1>
          <p className="hw-uat__subtitle">
            All User Acceptance Testing scripts, sign-off forms, and release notes for HarborWell Connect — {index?.total_docs ?? "…"} documents.
          </p>
        </div>
        {index?.current_release && (
          <button
            type="button"
            className="hw-uat__current-release"
            onClick={() => setSelectedSlug(index.current_release.slug)}
            data-testid="admin-uat-current-release"
            title="Jump to the current release notes"
          >
            <Sparkles className="w-4 h-4" />
            <div className="hw-uat__current-release-text">
              <div className="hw-uat__current-release-label">Current release</div>
              <div className="hw-uat__current-release-title">{index.current_release.title}</div>
            </div>
          </button>
        )}
      </div>

      <div className="hw-uat__body">
        {/* -------------------------- SIDEBAR -------------------------- */}
        <aside className="hw-uat__sidebar no-print" data-testid="admin-uat-sidebar">
          <div className="hw-uat__search">
            <Search className="w-4 h-4" />
            <input
              type="search"
              placeholder="Search all release docs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-uat-search-input"
            />
            {searching && <Loader2 className="w-4 h-4 animate-spin" />}
            {query && !searching && (
              <button type="button" onClick={() => setQuery("")}
                      className="hw-uat__search-clear" aria-label="Clear search"
                      data-testid="admin-uat-search-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {loadingIndex ? (
            <div className="hw-uat__sidebar-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            categoriesForSidebar.map((cat) => {
              const Icon = cat.__search ? Search : (CATEGORY_ICON[cat.id] || FileText);
              const isCollapsed = !cat.__search && collapsed[cat.id];
              return (
                <div key={cat.id} className="hw-uat__category" data-testid={`admin-uat-cat-${cat.id}`}>
                  <button type="button" className="hw-uat__category-header"
                          onClick={() => !cat.__search && toggleCategory(cat.id)}
                          data-testid={`admin-uat-cat-toggle-${cat.id}`}>
                    {!cat.__search && (isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                    <Icon className="w-4 h-4" />
                    <span className="hw-uat__category-label">{cat.label}</span>
                    <span className="hw-uat__category-count">{cat.docs?.length ?? 0}</span>
                  </button>
                  {!isCollapsed && (
                    <ul className="hw-uat__doc-list">
                      {cat.docs.map((d) => (
                        <li key={d.slug}>
                          <button
                            type="button"
                            className={`hw-uat__doc-link${selectedSlug === d.slug ? " is-active" : ""}`}
                            onClick={() => setSelectedSlug(d.slug)}
                            data-testid={`admin-uat-doc-${d.slug.replace(/[^a-z0-9]/gi,'-')}`}
                            title={d.rel_path}
                          >
                            <div className="hw-uat__doc-link-title">{d.title}</div>
                            {cat.__search && d.excerpt && (
                              <div className="hw-uat__doc-link-excerpt">
                                …{d.excerpt}…
                                <span className="hw-uat__doc-link-badge">{d.match_count} match{d.match_count > 1 ? "es" : ""}</span>
                              </div>
                            )}
                            {!cat.__search && (
                              <div className="hw-uat__doc-link-meta">{formatBytes(d.size_bytes)}</div>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </aside>

        {/* -------------------------- CONTENT -------------------------- */}
        <main className="hw-uat__content" ref={contentRef} data-testid="admin-uat-content">
          {loadingDoc && !doc ? (
            <div className="hw-uat__loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading document…</div>
          ) : doc ? (
            <>
              <div className="hw-uat__doc-header no-print">
                <div>
                  <div className="hw-uat__doc-crumb">
                    {doc.rel_path}
                  </div>
                  <h2 data-testid="admin-uat-doc-title">{doc.title}</h2>
                  <div className="hw-uat__doc-meta">
                    <Clock className="w-3.5 h-3.5" />
                    Last modified: {relativeDate(doc.modified_at)} · {formatBytes(doc.size_bytes)}
                  </div>
                </div>
                <div className="hw-uat__doc-actions">
                  <button type="button" onClick={loadHistory}
                          className="hw-uat__btn hw-uat__btn--ghost"
                          data-testid="admin-uat-history-btn">
                    <History className="w-4 h-4" /> Version history
                  </button>
                  <button type="button" onClick={handleDownloadRaw}
                          className="hw-uat__btn hw-uat__btn--ghost"
                          data-testid="admin-uat-download-btn">
                    <Download className="w-4 h-4" /> Download .md
                  </button>
                  <button type="button" onClick={handlePrint}
                          className="hw-uat__btn hw-uat__btn--primary"
                          data-testid="admin-uat-print-btn">
                    <Printer className="w-4 h-4" /> Print / Save PDF
                  </button>
                </div>
              </div>

              {historyOpen && (
                <div className="hw-uat__history-panel no-print" data-testid="admin-uat-history-panel">
                  <div className="hw-uat__history-header">
                    <History className="w-4 h-4" />
                    <span>Version history</span>
                    <button type="button" onClick={() => setHistoryOpen(false)}
                            className="hw-uat__btn hw-uat__btn--ghost hw-uat__history-close"
                            data-testid="admin-uat-history-close">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {history?.entries?.length ? (
                    <ul className="hw-uat__history-list">
                      {history.entries.map((h) => (
                        <li key={h.commit}>
                          <div className="hw-uat__history-message">{h.message}</div>
                          <div className="hw-uat__history-meta">
                            <span>{h.author_name}</span>
                            <span>·</span>
                            <span>{relativeDate(h.committed_at)}</span>
                            <span>·</span>
                            <code>{h.commit.slice(0, 7)}</code>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="hw-uat__history-empty">
                      No git history available for this document yet.
                    </div>
                  )}
                </div>
              )}

              <article
                className="hw-uat__markdown"
                data-testid="admin-uat-markdown"
                dangerouslySetInnerHTML={{
                  __html: (doc.release_metadata_html || "") + doc.html,
                }}
              />
            </>
          ) : (
            <div className="hw-uat__empty">
              <BookOpen className="w-6 h-6" />
              <div>Select a document from the sidebar to begin.</div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
